import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  Flex,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import SearchInput from "../SearchInput/SearchInput";

import RangeSliderCustom from "../RangeSlider/RangeSlider";
import useUpdateFiltersData from "../../hooks/useUpdateFiltersData";
import { memo, useCallback } from "react";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { getCurrentColor } from "../../constants";
import { sortData } from "../../utils/sortData";
import { useAtom } from "jotai/index";
import {
  categoriesPageModeAtom,
  selectedFiltersAtom,
} from "../../atom/listings";
import { selectedFiltersAtom as productsSelectedFiltersAtom } from "../../atom/products";
import useFilter from "../../hooks/useFilter";

const Filter = ({ checkbox, data, title, slider, search, query }) => {
  const { colorMode } = useColorMode();
  const [categoriesPageMode] = useAtom(categoriesPageModeAtom);
  const isListingsMode = categoriesPageMode === "listings";
  const [selectedFilters] = useAtom(
    isListingsMode ? selectedFiltersAtom : productsSelectedFiltersAtom,
  );
  const { AddOrUpdateFilter } = useFilter();
  const isBW = query === "bw";

  const {
    filteredData,
    setSearchQuery,
    searchQuery,
    setRangeValues,
    rangeValues,
  } = useUpdateFiltersData(sortData(data), search, slider);

  const handleCheckboxChange = useCallback(
    (currentValue, flag, id, brand_id, vendor_id) => {
      const valueToCheck =
        (id || brand_id || vendor_id || flag || currentValue) + "";

      const isSelected =
        selectedFilters[query]?.some((item) => {
          const itemValue =
            item.id ||
            item.brand_id ||
            item.vendor_id ||
            item.flag ||
            item.value;
          return (
            (itemValue + "").toLowerCase() ===
            (isBW ? currentValue : valueToCheck + "").toLowerCase()
          );
        }) || false;
      AddOrUpdateFilter(
        query,
        isBW ? currentValue : valueToCheck,
        isSelected ? "remove" : "add",
      );
    },
    [query, selectedFilters, AddOrUpdateFilter, isBW],
  );

  const Row = useCallback(
    ({ index, style }) => {
      const {
        name,
        count,
        icon,
        brand_name,
        vendor_name,
        brand_id,
        vendor_id,
        flag,
        alt_name,
        id,
      } = filteredData[index];

      const currentValue =
        (alt_name || flag || name || brand_name || vendor_name) + "";
      const valueToCheck = id || brand_id || vendor_id || flag || currentValue;

      const checkFilter =
        selectedFilters[query]?.some((filter) => {
          const filterValue =
            filter?.id ||
            filter?.brand_id ||
            filter?.vendor_id ||
            filter?.flag ||
            filter?.value;
          return (
            (filterValue + "").toLowerCase() ===
            ((isBW ? currentValue : valueToCheck) + "").toLowerCase()
          );
        }) || false;

      return (
        <Box style={style}>
          <Checkbox
            isChecked={checkFilter}
            sx={{
              ".chakra-checkbox__label": {
                width: "100%",
              },
              ".chakra-checkbox__control": {
                cursor: "pointer",
              },
              ".chakra-checkbox__label > *": {
                cursor: "pointer",
              },
              "&": {
                width: "100%",
                cursor: "pointer",
              },
            }}
            display={"flex"}
            w={"100%"}
            key={flag || name || brand_id || vendor_id}
            py={"5px"}
            my={"5px"}
            gap={"10px"}
            pr={"7px"}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCheckboxChange(currentValue, flag, id, brand_id, vendor_id);
            }}
            cursor="pointer"
          >
            <Flex
              alignItems={"center"}
              w={"90%"}
              gap={"5px"}
              justifyContent={"space-between"}
            >
              <Flex
                justifyContent={"space-between"}
                w={"100%"}
                alignItems={"center"}
                maxW={icon ? "84px" : "auto"}
              >
                <Text
                  fontSize={"xs"}
                  textTransform={"capitalize"}
                  fontFamily={"mainFont"}
                  wordBreak={"break-word"}
                  fontWeight={"400"}
                >
                  {name || brand_name || vendor_name}
                </Text>
                {icon}
              </Flex>
              {count !== 0 && (
                <Text
                  fontSize={"13px"}
                  fontFamily={"mainFont"}
                  fontWeight={"400"}
                >
                  {count}
                </Text>
              )}
            </Flex>
          </Checkbox>
        </Box>
      );
    },
    [filteredData, selectedFilters, query, handleCheckboxChange, isBW],
  );

  const sortedData =
    title === "Number of Rounds"
      ? filteredData.sort((a, b) => +b.name - +a.name)
      : filteredData;

  return (
    <Flex w={"100%"}>
      <Accordion allowMultiple w={"100%"} defaultIndex={[0]}>
        <AccordionItem
          w={"100%"}
          borderRadius={{ base: "0px", lg: "6px" }}
          border={{ base: "none", lg: "1px solid" }}
          height={"100%"}
          borderColor={getCurrentColor(
            colorMode,
            "grayC.500 !important",
            "transparent !important",
          )}
          _hover={{ bg: getCurrentColor(colorMode, "grayC.600", "grayC.1700") }}
        >
          <AccordionButton
            bg={getCurrentColor(colorMode, "grayC.600", "grayC.1700")}
            borderTopRadius={{ base: "0px", lg: "6px" }}
          >
            <Box
              as="span"
              flex="1"
              color={getCurrentColor(colorMode, "black.200", "grayC.1600")}
              textAlign="left"
            >
              {title}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel
            pt={
              !slider && !search
                ? { base: "10px", md: "10px" }
                : { base: "21px", md: "16px" }
            }
            maxH={"254px"}
            height={"100%"}
            pb={4}
            pr={3}
            w={"100%"}
            overflow={"hidden"}
            h={"100%"}
            bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1900")}
            borderBottomRadius={{ base: "0px", lg: "6px" }}
          >
            {slider && filteredData.length && (
              <RangeSliderCustom
                data={filteredData}
                updateRange={setRangeValues}
                values={rangeValues}
                title={title}
              />
            )}
            {search && (
              <Box mb={"18px"}>
                <SearchInput
                  title={title}
                  value={searchQuery}
                  handleChange={setSearchQuery}
                  border={"1px solid"}
                  borderColor={getCurrentColor(
                    colorMode,
                    "grayC.500",
                    "grayC.2400",
                  )}
                  display={"block"}
                  pl={"42px"}
                  name={title}
                />
              </Box>
            )}

            <Box minH={filteredData.length < 4 ? "125px" : "154px"} h={"auto"}>
              {checkbox && filteredData.length > 0 ? (
                <AutoSizer>
                  {({ height, width }) => (
                    <List
                      width={width}
                      height={height}
                      itemCount={filteredData.length}
                      itemSize={50}
                      itemData={sortedData}
                      overscanCount={5}
                      initialScrollOffset={0}
                    >
                      {Row}
                    </List>
                  )}
                </AutoSizer>
              ) : (
                <Text fontSize={"xs"}>Filter not found</Text>
              )}
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Flex>
  );
};

export default memo(Filter);
